import React from 'react';

import * as Style from './style.module.scss';

import Form, { useFormContext } from '../../../../components/Form';
import { formatPhoneNumber } from '@hero/hero-utils/phone';
import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
// import GeneralStickyNavbar from './GeneralStickyNavbar';
import PersonalForm from './Forms/PersonalForm';
import ContactForm from './Forms/ContactForm';
import DeviceValuesBox, { hasDeviceValues } from './components/DeviceValuesBox';
import { showDeviceValuesOnDemographics } from '../../../../constants/featureFlags';
import { MemberUpdateFormSchemaParams, memberUpdateFormSchema } from './validator';
import prepareState from '../../../../utils/prepareState';
import UnsavedChangesModal from '../../../LeadsManagement/Details/v2/components/UnsavedChangesModal';
import { OrganizationMemberDetails } from '../../types';
import useUnsavedChangeBlocker from './useUnsavedChangeBlocker';
import StickyNavbar from './StickyNavbar';
import Caregivers from './components/Caregivers';
import useMemberUpdate from '../../api/useMemberUpdate';
import { useOrganizationMemberDetailsContext } from '../context/OrganizationMemberDetailsContext';
import { getDOBFormat } from '../../../../utils/date';
import { useUserStatusContext } from '../../../../context/UserStatus';
import useGetCustomDemographics from '../../api/useGetCustomDemographics';
import useUpdateCustomDemographics from '../../api/useUpdateCustomDemographics';
import { CustomDemographics } from '../../types';
import SupplementalForm from './Forms/SupplementalForm';
import {
    SupplementalFormValues,
    hasChangedFrom,
    supplementalSchema,
    toSupplementalFormValues,
    toSupplementalPayload
} from './utils/supplemental';

interface GeneralProps {
    member?: OrganizationMemberDetails;
    onSuccess?: () => void;
}

interface GeneralInnerProps {
    member?: OrganizationMemberDetails;
    isLoading?: boolean;
    isSuccess?: boolean;
    isError?: boolean;
    errorType?: string;
    supplementalDemographics?: CustomDemographics | null;
    isSupplementalLoading?: boolean;
    supplementalErrorMessage?: string;
}

const getDefaultValues = (member: OrganizationMemberDetails) => ({
    address_line_1: member?.member_info?.address_info?.address_line_1 || '',
    address_line_2: member?.member_info?.address_info?.address_line_2 || '',
    city: member?.member_info?.address_info?.city || '',
    state: member?.member_info?.address_info?.state || '',
    zip: member?.member_info?.address_info?.zip || '',
    date_of_birth: getDOBFormat(member?.member_info?.contact_info?.date_of_birth),
    first_name: member.member_info.first_name || '',
    last_name: member.member_info.last_name || '',
    email: member.member_info.contact_info.email || '',
    phone: member.member_info.contact_info.phone_number || '',
    external_user_id: member.member_info.external_user_id || ''
});

const getMemberUpdateError = (errorType?: string) => {
    const errors: Record<string, string> = {
        serial_not_exist: "Device serial doesn't exists",
        email_not_exist: "Email doesn't exists"
    };

    return errorType && errors[errorType]
        ? errors[errorType]
        : 'Something went wrong. Please contact customer support.';
};

const getUpdateMemberPayload = (attributes: MemberUpdateFormSchemaParams) => {
    const formattedDateOfBirth = attributes.date_of_birth
        ? attributes.date_of_birth.replace(/-/g, '/')
        : undefined;

    return {
        external_user_id: attributes.external_user_id || undefined,
        contact: {
            first_name: attributes.first_name || undefined,
            last_name: attributes.last_name || undefined,
            date_of_birth: formattedDateOfBirth
                ? getDOBFormat(formattedDateOfBirth, 'yyyy-MM-dd')
                : undefined,
            email: attributes.email || undefined,
            cell_phone: attributes.phone ? formatPhoneNumber(attributes.phone) : undefined
        },
        address: {
            address_line_1: attributes.address_line_1 || undefined,
            address_line_2: attributes?.address_line_2 || undefined,
            state: attributes.state ? prepareState(attributes.state) : undefined,
            city: attributes.city || undefined,
            zip: attributes.zip || undefined
        }
    };
};

const DemographicsInner: React.FC<GeneralInnerProps> = ({
    member,
    isLoading = false,
    errorType,
    isSuccess = false,
    isError = false,
    supplementalDemographics,
    isSupplementalLoading = false,
    supplementalErrorMessage
}) => {
    const [showBlockerModal, setShowBlockerModal] = React.useState(false);
    const layoutRef = React.useRef<HTMLDivElement>(null);

    const { permissions } = useUserStatusContext();

    const isDischarged = !!member?.member_info.status_info?.discharged_at;
    const isCancelled = member?.member_info.status_info?.status === 'CANCELLED';

    const canEdit = isDischarged || isCancelled ? false : permissions.fleet.fleetMemberEdit;

    const { memberDetails } = useOrganizationMemberDetailsContext();

    const { caregivers } = memberDetails;

    const hasCaregivers = caregivers && caregivers.length > 0;

    const {
        formState: { dirtyFields },
        resetField
    } = useFormContext();

    // Per-field so a refetch of one API's data doesn't discard unsaved edits to the other's.
    const resetFields = React.useCallback(
        (values: object) =>
            Object.entries(values).forEach(([name, defaultValue]) =>
                resetField(name, { defaultValue })
            ),
        [resetField]
    );

    React.useEffect(() => {
        member && resetFields(getDefaultValues(member));
    }, [member, resetFields]);

    React.useEffect(() => {
        resetFields(toSupplementalFormValues(supplementalDemographics));
    }, [supplementalDemographics, resetFields]);

    const isChanged = Object.keys(dirtyFields).length !== 0;

    const { handleCancel, handleDiscard } = useUnsavedChangeBlocker({
        isChanged,
        isSuccess,
        changeBlockerModal: setShowBlockerModal
    });

    const [showStickyNavbar, setShowStickyNavbar] = React.useState(false);

    const handleOnScroll = () => {
        if (layoutRef.current) {
            const top = layoutRef.current.getBoundingClientRect().top;

            setShowStickyNavbar(top < 0);
        }
    };

    React.useEffect(() => {
        document.addEventListener('scroll', handleOnScroll);

        return () => {
            document.removeEventListener('scroll', handleOnScroll);
        };
    }, []);

    return (
        <>
            <div style={{ position: 'relative' }}>
                <StickyNavbar
                    member={member}
                    showStickyNavbar={showStickyNavbar}
                    isLoading={isLoading}
                    isChanged={isChanged}
                />
                <Section noDefaultPadding paddingHorizontal="regular">
                    <div className={Style.layout} ref={layoutRef}>
                        <div className={Style.column}>
                            <div className={`${Style.section} ${Style.patientSection}`}>
                                <div className={Style.patientPanes}>
                                    <div className={Style.personalPane}>
                                        <PersonalForm canEdit={canEdit} />
                                    </div>
                                    {showDeviceValuesOnDemographics &&
                                    hasDeviceValues(member?.member_info) ? (
                                        <div className={Style.devicePane}>
                                            <DeviceValuesBox
                                                memberInfo={member?.member_info}
                                                compact
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            <div className={Style.section}>
                                <ContactForm canEdit={canEdit} />
                            </div>
                            {hasCaregivers ? (
                                <div className={Style.section}>
                                    <Caregivers />
                                </div>
                            ) : null}
                        </div>

                        <div className={Style.column}>
                            <div className={Style.section}>
                                <SupplementalForm
                                    savedValues={toSupplementalFormValues(supplementalDemographics)}
                                    canEdit={canEdit && !isSupplementalLoading}
                                    errorMessage={supplementalErrorMessage}
                                />
                            </div>
                        </div>
                    </div>

                    {isChanged ? (
                        <section className={`${Style.btnSection}`}>
                            <Button type="submit" disabled={isLoading}>
                                Save
                            </Button>
                        </section>
                    ) : null}
                </Section>
            </div>
            <UnsavedChangesModal
                showBlockerModal={showBlockerModal}
                onCancel={handleCancel}
                onSave={handleCancel}
                saveBtnLabel="Go Back to Edit"
                isLoading={isLoading}
                onDiscard={handleDiscard}
                isError={isError}
                errorMessage={getMemberUpdateError(errorType)}
            />
        </>
    );
};

const Demographics: React.FC<GeneralProps> = ({ member, onSuccess }) => {
    const memberId = member?.member_info.member_id;
    const { data: supplementalData, isLoading: isSupplementalLoading } =
        useGetCustomDemographics(memberId);
    const supplementalDemographics = supplementalData ?? null;

    const memberUpdate = useMemberUpdate({ member_id: memberId }, onSuccess);
    const supplementalUpdate = useUpdateCustomDemographics(memberId);

    const handleSubmit = (attributes: MemberUpdateFormSchemaParams & SupplementalFormValues) => {
        memberUpdate.reset();
        supplementalUpdate.reset();

        if (member && hasChangedFrom(attributes, getDefaultValues(member))) {
            memberUpdate.mutate({ payload: getUpdateMemberPayload(attributes) });
        }

        if (hasChangedFrom(attributes, toSupplementalFormValues(supplementalDemographics))) {
            supplementalUpdate.mutate(toSupplementalPayload(attributes));
        }
    };

    const firedUpdates = [memberUpdate, supplementalUpdate].filter((update) => !update.isIdle);

    return (
        <Form
            compact
            validationMode="onTouched"
            submitFn={handleSubmit}
            validationSchema={memberUpdateFormSchema.concat(supplementalSchema)}
            defaultValues={{
                ...(member && getDefaultValues(member)),
                ...toSupplementalFormValues(supplementalDemographics)
            }}
        >
            <DemographicsInner
                member={member}
                isLoading={memberUpdate.isPending || supplementalUpdate.isPending}
                errorType={memberUpdate.error?.errors[0]}
                isSuccess={
                    firedUpdates.length > 0 && firedUpdates.every((update) => update.isSuccess)
                }
                isError={memberUpdate.isError || supplementalUpdate.isError}
                supplementalDemographics={supplementalDemographics}
                isSupplementalLoading={isSupplementalLoading}
                supplementalErrorMessage={supplementalUpdate.error?.errors?.[0]}
            />
        </Form>
    );
};

export default Demographics;
